// Browser side of FcCore::SignupBotChecker: the server only trusts a registration that
// carries a token fetched by this script a couple of seconds earlier.

const TOKEN_URL = "/sign_up/bot_check_token"
const DEFAULT_PURPOSE = "sign_up"
const FETCH_TIMEOUT_MS = 5000
// The server wants 2 s between issuing a token and using it.
const MIN_FILL_MS = 2300
// Server-side tokens live for an hour.
const MAX_AGE_MS = 50 * 60 * 1000

// A token only passes the check of the form (purpose) it was fetched for.
const pending = {}

async function fetchToken(purpose) {
  let timeout
  try {
    const url =
      purpose === DEFAULT_PURPOSE
        ? TOKEN_URL
        : `${TOKEN_URL}?purpose=${encodeURIComponent(purpose)}`
    const request = fetch(url, {
      credentials: "include",
      headers: { Accept: "application/json" },
    })
      .then((response) => (response.ok ? response.json() : null))
      .then((body) => (body && body.token ? { token: body.token, fetchedAt: Date.now() } : null))

    return await Promise.race([
      request,
      new Promise((resolve) => {
        timeout = window.setTimeout(() => resolve(null), FETCH_TIMEOUT_MS)
      }),
    ])
  } catch (error) {
    return null
  } finally {
    window.clearTimeout(timeout)
  }
}

// Call when a sign-up form opens, so that the token is old enough once the form is submitted.
export function prepareBotCheckToken(purpose = DEFAULT_PURPOSE) {
  if (!pending[purpose]) {
    const request = fetchToken(purpose).then((entry) => {
      if (!entry && pending[purpose] === request) pending[purpose] = null
      return entry
    })
    pending[purpose] = request
  }
  return pending[purpose]
}

// Tokens are single-use, so each one is forgotten as soon as it is handed out.
async function takeBotCheckToken(purpose) {
  let entry = await prepareBotCheckToken(purpose)
  if (entry && Date.now() - entry.fetchedAt > MAX_AGE_MS) {
    pending[purpose] = null
    entry = await prepareBotCheckToken(purpose)
  }
  pending[purpose] = null
  if (!entry) return null

  const wait = MIN_FILL_MS - (Date.now() - entry.fetchedAt)
  if (wait > 0) await new Promise((resolve) => window.setTimeout(resolve, wait))
  return entry.token
}

// `send(token)` posts the registration and resolves to { response, body }.
// It is called a second time, with a fresh token, when the server answers "retryable".
export async function withBotCheckToken(send, purpose = DEFAULT_PURPOSE) {
  const result = await send(await takeBotCheckToken(purpose))
  if (!(result.body && result.body.retryable)) return result

  return send(await takeBotCheckToken(purpose))
}

// For forms that the browser submits itself (no fetch): the token is fetched when the visitor
// first touches the form and is put into its `bot_check_token` field right before the real
// submit. The server re-renders such a form with an error message when the check fails.
export function attachBotCheckToForm(form, purpose = DEFAULT_PURPOSE) {
  const field = form.querySelector('input[name="bot_check_token"]')
  if (!field) return

  let submitting = false
  form.addEventListener("focusin", () => prepareBotCheckToken(purpose), { once: true })
  form.addEventListener("submit", async (event) => {
    event.preventDefault()
    if (submitting) return

    submitting = true
    field.value = (await takeBotCheckToken(purpose)) || ""
    form.submit()
  })
}
